import { Fragment, useCallback, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import type { SearchEvent, SearchScope, SortDir } from '../api/searchTypes';
import { eventHref } from '../../board/board/routes';
import { NEW_TAB_LINK_PROPS } from '../../../../utils/newTab';
import { EventOutcomesPanel } from '../../board/card/EventOutcomesPanel';
import { HoverPanel } from '../../board/card/HoverPanel';
import { gridTemplate, resultColumns } from './resultColumns';
import {
    MARKET_SORT_KEYS,
    MARKET_SORT_LABELS,
    isDependent,
    pickPriceMarket,
    sortMarkets,
    type MarketSortKey,
} from './marketOrder';
import { MarketRow } from './MarketRow';
import { RowChartPanel } from './RowChartPanel';
import { RowImage } from './RowImage';
import { SearchExportButton } from './SearchExportButton';
import { searchEventPanel } from './searchEventPanel';
import { exportMarketsOf } from './searchExport';
import { SeriesBadge } from './SeriesBadge';
import { StatusCell } from './StatusCell';
import { TagChip, type TagState } from './TagChip';

export interface EventRowProps {
    event: SearchEvent;
    scope: SearchScope;
    statusFilter: string;
    primaryTags: string[];
    tagState: (tag: string) => TagState;
    categoryState: (category: string) => TagState;
    seriesActive: boolean;
    selected: Set<string>;
    showRaw: boolean;
    onIncludeTag: (tag: string) => void;
    onExcludeTag: (tag: string) => void;
    onIncludeCategory: (category: string) => void;
    onExcludeCategory: (category: string) => void;
    onSelectSeries: (slug: string) => void;
    onToggleMarket: (marketId: string) => void;
    onSetMarkets: (marketIds: string[], value: boolean) => void;
}

const SELECT_SX = {
    fontSize: '10px',
    fontFamily: 'inherit',
    color: 'text.secondary',
    bgcolor: 'transparent',
    border: 'none',
    outline: 'none',
    p: 0,
    m: 0,
    height: 14,
    lineHeight: '14px',
    cursor: 'pointer',
} as const;

const PANEL_OPEN_DELAY_MS = 150;

export function EventRow({
    event,
    scope,
    statusFilter,
    primaryTags,
    tagState,
    categoryState,
    seriesActive,
    selected,
    showRaw,
    onIncludeTag,
    onExcludeTag,
    onIncludeCategory,
    onExcludeCategory,
    onSelectSeries,
    onToggleMarket,
    onSetMarkets,
}: EventRowProps) {
    const theme = useTheme();
    const columns = resultColumns();
    const [expanded, setExpanded] = useState(scope === 'market');
    const [showRest, setShowRest] = useState(false);
    const [sortKey, setSortKey] = useState<MarketSortKey>('default');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [openChart, setOpenChart] = useState<string | null>(null);

    const priceMarket = useMemo(
        () => pickPriceMarket(event.markets),
        [event.markets],
    );

    const dependent = useMemo(
        () => isDependent(event.markets.filter(m => m.yes_prob != null)),
        [event.markets],
    );

    const priceOutcome =
        priceMarket?.group_item_title ??
        priceMarket?.outcome_yes_label ??
        (dependent ? null : 'Yes');

    const openOnly = statusFilter === 'open';

    const { headline, rest } = useMemo(() => {
        const matched =
            scope === 'market' ? event.markets.filter(m => m.matched) : event.markets;
        const unmatched =
            scope === 'market' ? event.markets.filter(m => !m.matched) : [];
        const head = openOnly ? matched.filter(m => m.is_open) : matched;
        const tail = openOnly
            ? [...matched.filter(m => !m.is_open), ...unmatched]
            : unmatched;
        return {
            headline: sortMarkets(head, sortKey, sortDir),
            rest: sortMarkets(tail, sortKey, sortDir),
        };
    }, [event.markets, scope, openOnly, sortKey, sortDir]);

    const restLabel = openOnly
        ? scope === 'market'
            ? 'other'
            : 'closed/resolved'
        : 'non-matching';

    const visible = showRest ? [...headline, ...rest] : headline;

    const marketIds = useMemo(
        () => event.markets.map(m => m.market_id),
        [event.markets],
    );
    const selectedCount = marketIds.filter(id => selected.has(id)).length;
    const allSelected = marketIds.length > 0 && selectedCount === marketIds.length;
    const someSelected = selectedCount > 0 && !allSelected;

    const exportMarkets = useMemo(() => exportMarketsOf(event), [event]);

    const renderPanel = useCallback(
        () => (
            <EventOutcomesPanel
                event={searchEventPanel(event)}
                lastTrade={false}
            />
        ),
        [event],
    );

    const headerTags = useMemo(() => {
        const strong = event.tags.filter(t => primaryTags.includes(t));
        const weak = event.tags.filter(t => !primaryTags.includes(t));
        return [...strong, ...weak].slice(0, 6);
    }, [event.tags, primaryTags]);

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box
                onClick={() => setExpanded(v => !v)}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate(),
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1.25,
                    py: 0.75,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                    <IconButton size="small" sx={{ p: 0.25, mt: '1px' }}>
                        {expanded ? (
                            <ExpandMoreIcon sx={{ fontSize: 15 }} />
                        ) : (
                            <ChevronRightIcon sx={{ fontSize: 15 }} />
                        )}
                    </IconButton>

                    <RowImage src={event.image} size={24} />

                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <HoverPanel
                                disabled={event.markets.length === 0}
                                placement="right-start"
                                padded={false}
                                openDelayMs={PANEL_OPEN_DELAY_MS}
                                content={renderPanel}
                                sx={{ display: 'flex', minWidth: 0 }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {event.title ?? event.event_id}
                                </Typography>
                            </HoverPanel>
                            <Tooltip title="Open event" disableInteractive>
                                <IconButton
                                    size="small"
                                    component="a"
                                    href={eventHref(event.slug ?? event.event_id)}
                                    {...NEW_TAB_LINK_PROPS}
                                    onClick={e => e.stopPropagation()}
                                    sx={{ p: 0.2 }}
                                >
                                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.4,
                                flexWrap: 'wrap',
                            }}
                        >
                            {event.series && (
                                <SeriesBadge
                                    label={event.series.title ?? event.series.slug}
                                    recurrence={event.series.recurrence}
                                    active={seriesActive}
                                    onClick={() => onSelectSeries(event.series!.slug)}
                                />
                            )}
                            {event.category && (
                                <TagChip
                                    label={event.category}
                                    strong
                                    state={categoryState(event.category)}
                                    onInclude={onIncludeCategory}
                                    onExclude={onExcludeCategory}
                                />
                            )}
                            {headerTags.map(tag => (
                                <TagChip
                                    key={tag}
                                    label={tag}
                                    state={tagState(tag)}
                                    onInclude={onIncludeTag}
                                    onExclude={onExcludeTag}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>

                {columns.map(column => {
                    if (column.key === 'status') {
                        return (
                            <StatusCell key={column.key} status={event.status} strong />
                        );
                    }

                    if (column.key === 'price') {
                        return (
                            <Box
                                key={column.key}
                                sx={{
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {column.eventValue(event, priceMarket, scope)}
                                </Typography>
                                {priceOutcome && (
                                    <Tooltip
                                        title={priceOutcome}
                                        placement="top"
                                        disableInteractive
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.disabled',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '100%',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {priceOutcome}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </Box>
                        );
                    }

                    return (
                        <Typography
                            key={column.key}
                            sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                textAlign: column.align === 'left' ? 'left' : 'right',
                                color: 'text.primary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {column.eventValue(event, priceMarket, scope)}
                        </Typography>
                    );
                })}

                <Box
                    onClick={e => e.stopPropagation()}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '2px',
                    }}
                >
                    <SearchExportButton
                        markets={exportMarkets}
                        tooltip="Download the data of this event"
                    />
                    <Tooltip title="Show chart" disableInteractive enterDelay={600}>
                        <Box
                            onClick={e => {
                                e.stopPropagation();
                                setOpenChart(prev =>
                                    prev === 'event' ? null : 'event',
                                );
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 20,
                                height: 20,
                                borderRadius: '3px',
                                cursor: 'pointer',
                                color:
                                    openChart === 'event'
                                        ? 'primary.main'
                                        : 'text.disabled',
                                bgcolor:
                                    openChart === 'event'
                                        ? alpha(theme.palette.primary.main, 0.12)
                                        : 'transparent',
                                '&:hover': { color: 'primary.main' },
                            }}
                        >
                            <CandlestickChartIcon sx={{ fontSize: 14 }} />
                        </Box>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip
                        title="Select every market of this event"
                        disableInteractive
                        enterDelay={600}
                    >
                        <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onClick={e => e.stopPropagation()}
                            onChange={() => onSetMarkets(marketIds, !allSelected)}
                            sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 15 } }}
                        />
                    </Tooltip>
                </Box>
            </Box>

            <Collapse in={openChart === 'event'} unmountOnExit>
                <Box
                    sx={{
                        px: 1.25,
                        pt: 1,
                        borderTop: '1px solid',
                        borderColor: alpha(theme.palette.divider, 0.5),
                    }}
                >
                    <RowChartPanel event={event} />
                </Box>
            </Collapse>

            <Collapse in={expanded} unmountOnExit>
                <Box sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            pl: 6.25,
                            height: 22,
                            borderTop: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.5),
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>
                            {dependent ? 'One outcome wins · sort by' : 'Sort by'}
                        </Typography>
                        <Box
                            component="select"
                            value={sortKey}
                            onChange={e =>
                                setSortKey(
                                    (e.target as HTMLSelectElement).value as MarketSortKey,
                                )
                            }
                            sx={SELECT_SX}
                        >
                            {MARKET_SORT_KEYS.map(key => (
                                <option key={key} value={key}>
                                    {MARKET_SORT_LABELS[key]}
                                </option>
                            ))}
                        </Box>
                        <Box
                            onClick={() =>
                                setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'))
                            }
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: sortKey === 'default' ? 'default' : 'pointer',
                                color: 'text.disabled',
                                opacity: sortKey === 'default' ? 0.35 : 1,
                            }}
                        >
                            {sortDir === 'desc' ? (
                                <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                            ) : (
                                <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                            )}
                        </Box>
                    </Box>

                    {visible.map(market => (
                        <Fragment key={market.market_id}>
                            <MarketRow
                                market={market}
                                tagState={tagState}
                                selected={selected.has(market.market_id)}
                                showRaw={showRaw}
                                onIncludeTag={onIncludeTag}
                                onExcludeTag={onExcludeTag}
                                onToggleSelect={onToggleMarket}
                                chartOpen={openChart === market.market_id}
                                onToggleChart={id =>
                                    setOpenChart(prev =>
                                        prev === id ? null : id,
                                    )
                                }
                            />
                            <Collapse
                                in={openChart === market.market_id}
                                unmountOnExit
                            >
                                <Box sx={{ px: 1.25, pt: 1 }}>
                                    <RowChartPanel
                                        event={event}
                                        marketId={market.market_id}
                                    />
                                </Box>
                            </Collapse>
                        </Fragment>
                    ))}
                    {rest.length > 0 && (
                        <Box sx={{ px: 1.25, py: 0.4 }}>
                            <Button
                                size="small"
                                onClick={() => setShowRest(v => !v)}
                                sx={{ fontSize: '10px' }}
                            >
                                {showRest
                                    ? `Hide ${rest.length} ${restLabel} market${rest.length === 1 ? '' : 's'}`
                                    : `Show ${rest.length} ${restLabel} market${rest.length === 1 ? '' : 's'}`}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}